import { getAppVersion, getProductName } from '../../utils/device';
import BDSocketManager, {
  type BDSocketConfig,
  type MessageHandler,
  type SubscribeId,
  type WebSocketMessage,
} from './BDSocketManager';

export type {
  SubscribeId,
  AppPlatform,
  ClientType,
  BDSocketConfig,
  MessageCallback,
  MessageHandler,
  WebSocketMessage,
  PushTokenInfo,
  ConnectionState,
} from './BDSocketManager';

export { default as BDSocketManager } from './BDSocketManager';

/**
 * 全局WebSocket服务
 * 职责：管理全局单例的 BDSocketManager，提供简化的API
 *
 * 所有消息订阅 / 事件监听均存储在服务层，manager 销毁-重建后仍有效。
 * BDSocketManager 仅通过 onMessage / onStateChange 回调向服务层推送数据。
 */
type ServiceEventType = 'state_change' | 'message';
type ServiceEventCallback = (data: unknown) => void;

class WebSocketService {
  private manager: BDSocketManager | null = null;
  private storedDeviceId?: string;

  /** 服务层消息订阅（reinitialize 后仍有效） */
  private messageSubscriptions: Map<SubscribeId, MessageHandler<unknown>> = new Map();

  /** 服务层事件监听（reinitialize 后仍有效，manager 为 null 时也能注册） */
  private serviceEventListeners: Map<ServiceEventType, Set<ServiceEventCallback>> = new Map();

  // 页面可见性管理相关属性
  private backgroundTimerId: ReturnType<typeof setTimeout> | null = null;
  private visibilityHandler: (() => void) | null = null;
  private wasConnectedBeforeBackground: boolean = false;
  private isVisibilityListenerSetup: boolean = false;

  // 后台断开连接的延迟时间（5分钟）
  private readonly BACKGROUND_DISCONNECT_DELAY = 5 * 60 * 1000;

  /** 将消息转发给所有服务层订阅者 */
  private forwardToSubscriptions = (msg: WebSocketMessage<unknown>): void => {
    this.messageSubscriptions.forEach((callback, id) => {
      try {
        callback(msg);
      } catch (error) {
        console.error(`WebSocketService: 消息回调执行失败 (ID: ${id}):`, error);
      }
    });
  };

  /** 向服务层事件监听者派发事件 */
  private emitToListeners = (event: ServiceEventType, data: unknown): void => {
    this.serviceEventListeners.get(event)?.forEach(cb => {
      try {
        cb(data);
      } catch (error) {
        console.error(`WebSocketService: 事件监听执行失败 (${event}):`, error);
      }
    });
  };

  /** 设置 manager 的回调，将消息/状态变更桥接到服务层 */
  private setupManagerCallbacks(): void {
    if (!this.manager) return;

    this.manager.onMessage = msg => {
      this.forwardToSubscriptions(msg);
      this.emitToListeners('message', msg);
    };

    this.manager.onStateChange = state => {
      this.emitToListeners('state_change', state);
    };
  }

  /** 清除 manager 上的回调 */
  private clearManagerCallbacks(): void {
    if (!this.manager) return;
    this.manager.onMessage = null;
    this.manager.onStateChange = null;
  }

  /**
   * 初始化WebSocket服务
   */
  initialize(customDeviceId?: string): BDSocketManager {
    if (this.manager) {
      if (customDeviceId && this.storedDeviceId && this.storedDeviceId !== customDeviceId) {
        return this.reinitialize(customDeviceId);
      }
      return this.manager;
    }

    if (!customDeviceId) {
      throw new Error('Device ID cannot be empty');
    }

    const config: BDSocketConfig = {
      product: getProductName(),
      deviceId: customDeviceId,
      appVersion: getAppVersion(),
      appPlatform: 'web',
    };

    this.manager = new BDSocketManager(config);
    this.storedDeviceId = customDeviceId;
    this.setupManagerCallbacks();

    return this.manager;
  }

  /**
   * 获取管理器实例
   */
  getManager(): BDSocketManager | null {
    return this.manager;
  }

  /**
   * 确保管理器已初始化
   */
  private ensureManager(): BDSocketManager {
    if (!this.manager) {
      console.error('WebSocket manager not initialized, please call initialize method first');
    }
    return this.manager as BDSocketManager;
  }

  /**
   * 连接WebSocket
   */
  async connect(): Promise<void> {
    const manager = this.ensureManager();
    await manager.connect();
    this.setupVisibilityListener();
  }

  /**
   * 断开连接
   */
  disconnect(): void {
    this.cancelBackgroundDisconnect();
    this.manager?.disconnect();
  }

  /**
   * 销毁服务（保留服务层订阅，以便 reinitialize 后恢复）
   */
  destroy(): void {
    this.cleanupVisibilityListener();
    if (this.manager) {
      this.clearManagerCallbacks();
      this.manager.disconnect();
      this.manager = null;
      this.storedDeviceId = undefined;
    }
  }

  /**
   * 完全销毁（清空服务层订阅 + 事件监听），用于 logout 等彻底清理场景
   */
  destroyAll(): void {
    this.destroy();
    this.messageSubscriptions.clear();
    this.serviceEventListeners.clear();
  }

  /**
   * 重新初始化
   */
  reinitialize(customDeviceId?: string): BDSocketManager {
    this.destroy();
    return this.initialize(customDeviceId);
  }

  /**
   * 获取连接状态
   */
  getConnectionState() {
    return (
      this.manager?.getConnectionState() ?? {
        isConnected: false,
        isConnecting: false,
        error: null,
      }
    );
  }

  /**
   * 添加消息订阅
   * 订阅存储在服务层，reinitialize 后仍有效（manager 为 null 时也可预先注册）
   */
  addMessageSubscription<T = string>(callback: MessageHandler<T>): SubscribeId {
    const id = `svc_sub_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    this.messageSubscriptions.set(id, callback as MessageHandler<unknown>);
    return id;
  }

  /**
   * 移除消息订阅
   */
  removeMessageSubscription(id: string): boolean {
    return this.messageSubscriptions.delete(id);
  }

  /**
   * 添加事件监听
   * 监听存储在服务层，manager 为 null 时也可预先注册，reinitialize 后仍有效
   */
  on(event: ServiceEventType, callback: ServiceEventCallback): void {
    if (!this.serviceEventListeners.has(event)) {
      this.serviceEventListeners.set(event, new Set());
    }
    this.serviceEventListeners.get(event)!.add(callback);
  }

  /**
   * 移除事件监听
   */
  off(event: ServiceEventType, callback: ServiceEventCallback): void {
    this.serviceEventListeners.get(event)?.delete(callback);
  }

  /**
   * 设置页面可见性监听
   */
  private setupVisibilityListener(): void {
    if (this.isVisibilityListenerSetup) {
      return;
    }

    if (typeof document === 'undefined' || !document.addEventListener) {
      return;
    }

    this.visibilityHandler = () => {
      this.handleVisibilityChange();
    };

    document.addEventListener('visibilitychange', this.visibilityHandler);
    this.isVisibilityListenerSetup = true;
  }

  /**
   * 处理页面可见性变化
   */
  private handleVisibilityChange(): void {
    if (typeof document === 'undefined') {
      return;
    }

    const isVisible = document.visibilityState === 'visible';

    if (isVisible) {
      this.cancelBackgroundDisconnect();

      if (this.wasConnectedBeforeBackground && this.manager) {
        const currentState = this.manager.getConnectionState();
        if (!currentState.isConnected && !currentState.isConnecting) {
          this.manager.connect().catch(error => {
            console.error('页面恢复后自动重连失败:', error);
          });
        }
      }

      this.wasConnectedBeforeBackground = false;
    } else {
      const currentState = this.getConnectionState();

      this.wasConnectedBeforeBackground = currentState.isConnected;

      if (currentState.isConnected) {
        this.scheduleBackgroundDisconnect();
      }
    }
  }

  /**
   * 安排后台断开连接
   */
  private scheduleBackgroundDisconnect(): void {
    this.cancelBackgroundDisconnect();

    this.backgroundTimerId = setTimeout(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        const currentState = this.getConnectionState();
        if (currentState.isConnected) {
          console.log('页面在后台超过5分钟，主动断开WebSocket连接');
          this.manager?.disconnect();
        }
      }

      this.backgroundTimerId = null;
    }, this.BACKGROUND_DISCONNECT_DELAY);
  }

  /**
   * 取消后台断开连接计划
   */
  private cancelBackgroundDisconnect(): void {
    if (this.backgroundTimerId !== null) {
      clearTimeout(this.backgroundTimerId);
      this.backgroundTimerId = null;
    }
  }

  /**
   * 清理页面可见性监听
   */
  private cleanupVisibilityListener(): void {
    this.cancelBackgroundDisconnect();

    if (this.isVisibilityListenerSetup && this.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
      this.isVisibilityListenerSetup = false;
    }

    this.wasConnectedBeforeBackground = false;
  }
}

// 导出全局单例
export const webSocketService = new WebSocketService();

// 便捷函数
export function initWebSocket(customDeviceId?: string): BDSocketManager {
  return webSocketService.initialize(customDeviceId);
}

export function getWebSocketManager(): BDSocketManager | null {
  return webSocketService.getManager();
}

export function connectWebSocket(): Promise<void> {
  return webSocketService.connect();
}

export function disconnectWebSocket(): void {
  webSocketService.disconnect();
}

export function destroyWebSocket(): void {
  webSocketService.destroyAll();
}

export default webSocketService;
